import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Check, X, Edit } from "lucide-react";
import { updateDocumentDisplayName } from "./document-utils";

interface Document {
  id: string;
  name: string;
  display_name?: string;
  file_path: string;
  section_id: string;
  created_at: string;
  size: number;
  position: number;
  content_type: string;
  created_by: string | null;
  creator_email: string | null;
  creator_name: string | null;
  organization_id: string;
}

interface DocumentDisplayNameEditProps {
  doc: Document;
  onDocumentUpdated: (docId: string, newDisplayName: string) => void;
}

export const DocumentDisplayNameEdit = ({ doc, onDocumentUpdated }: DocumentDisplayNameEditProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [newDisplayName, setNewDisplayName] = useState(doc.display_name || '');
  const [isLoading, setIsLoading] = useState(false);

  const handleSave = async () => {
    const trimmedName = newDisplayName.trim();
    
    if (trimmedName === (doc.display_name || '')) {
      setIsEditing(false);
      return;
    }

    setIsLoading(true);
    try {
      await updateDocumentDisplayName(doc.id, trimmedName || null);
      onDocumentUpdated(doc.id, trimmedName);
      setIsEditing(false);
    } catch (error) {
      setNewDisplayName(doc.display_name || ''); // Reset to original
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setNewDisplayName(doc.display_name || '');
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <div className="flex items-center gap-2 min-w-0 flex-1">
        <Input
          value={newDisplayName}
          onChange={(e) => setNewDisplayName(e.target.value)}
          className="h-8 text-sm min-w-0 flex-1"
          placeholder={doc.name}
          disabled={isLoading}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleSave();
            if (e.key === 'Escape') handleCancel();
          }}
          autoFocus
        />
        <Button
          size="sm"
          variant="ghost"
          onClick={handleSave}
          disabled={isLoading}
          className="h-8 w-8 p-0 flex-shrink-0"
        >
          <Check className="h-3 w-3" />
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={handleCancel}
          disabled={isLoading}
          className="h-8 w-8 p-0 flex-shrink-0"
        >
          <X className="h-3 w-3" />
        </Button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 group min-w-0 flex-1">
      <span className="text-sm font-medium truncate flex-1">
        {doc.display_name || doc.name}
      </span>
      <Button
        size="sm"
        variant="ghost"
        onClick={() => setIsEditing(true)}
        className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
      >
        <Edit className="h-3 w-3" />
      </Button>
    </div>
  );
};